// src/pages/help/Changelog.jsx — VERSÃO PÚBLICA (BLINDADA)
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Zap, Bug, Star, Shield, WifiOff,
  Palette, Smartphone, ChevronRight, Calendar, 
  GitBranch, Package, Award, Crown, TrendingUp, Gift
} from "lucide-react";
import { useIsMobile } from "../../hooks/useMediaQuery";

// ============================================================
// 🎨 BRANDING
// ============================================================
const BRAND = {
  primary: "#1a56db",
  primaryLight: "#3b82f6",
  primaryDark: "#1e3a5f",
  gold: "#c8a84e",
  goldLight: "#e2c97e",
  goldDark: "#8b6914",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  purple: "#8b5cf6",
};

// ============================================================
// 📝 TIPOS DE ATUALIZAÇÃO
// ============================================================
const TIPOS = {
  novo: { icon: Sparkles, color: "#8b5cf6", label: "Novidade", bg: "#8b5cf610" },
  melhoria: { icon: TrendingUp, color: "#3b82f6", label: "Melhoria", bg: "#3b82f610" },
  correcao: { icon: Bug, color: "#f59e0b", label: "Correção", bg: "#f59e0b10" },
  seguranca: { icon: Shield, color: "#10b981", label: "Segurança", bg: "#10b98110" },
  performance: { icon: Zap, color: "#ef4444", label: "Performance", bg: "#ef444410" },
  offline: { icon: WifiOff, color: "#6366f1", label: "Offline", bg: "#6366f110" },
  design: { icon: Palette, color: "#ec4899", label: "Design", bg: "#ec489910" },
  mobile: { icon: Smartphone, color: "#14b8a6", label: "Mobile", bg: "#14b8a610" },
};

// ============================================================
// 📋 CHANGELOG PÚBLICO (BLINDADO)
// ============================================================
const VERSOES = [
  {
    versao: "v12.0",
    data: "03/05/2026",
    destaque: true,
    titulo: "Central de Ajuda Completa + Sincronização entre Dispositivos",
    emoji: "🌟",
    mudancas: [
      { tipo: "novo", descricao: "Foto de perfil agora aparece em todos os seus dispositivos automaticamente" },
      { tipo: "novo", descricao: "Nova Central de Ajuda: Documentação completa, Tutorial Interativo, Suporte e Novidades" },
      { tipo: "novo", descricao: "Tutorial interativo com 6 módulos e certificado de conclusão" },
      { tipo: "novo", descricao: "Página de Suporte com formulário de contato, FAQ e status do sistema em tempo real" },
      { tipo: "melhoria", descricao: "Navegação mais intuitiva com busca rápida na documentação" },
      { tipo: "design", descricao: "Nova identidade visual azul premium com detalhes dourados" },
    ]
  },
  {
    versao: "v11.0",
    data: "02/05/2026",
    destaque: true,
    titulo: "Dashboard e Financeiro Mais Rápidos que Nunca",
    emoji: "⚡",
    mudancas: [
      { tipo: "novo", descricao: "Dashboard abre instantaneamente — os dados aparecem antes mesmo da internet responder" },
      { tipo: "novo", descricao: "Dashboard atualiza sozinho quando você cadastra imóveis em outras abas do navegador" },
      { tipo: "melhoria", descricao: "Painel financeiro carrega até 80% mais rápido que antes" },
      { tipo: "correcao", descricao: "Resolvido: ao recarregar a página, seus dados financeiros não desaparecem mais" },
      { tipo: "correcao", descricao: "Resolvido: gráficos do dashboard agora refletem novos registros imediatamente" },
    ]
  },
  {
    versao: "v10.0",
    data: "01/05/2026",
    destaque: false,
    titulo: "Estabilidade e Confiabilidade dos Seus Dados",
    emoji: "🔧",
    mudancas: [
      { tipo: "melhoria", descricao: "Dados financeiros agora aparecem na hora, sem esperar a nuvem processar" },
      { tipo: "correcao", descricao: "Resolvido: ao criar um lançamento, ele não sumia da lista até recarregar" },
      { tipo: "correcao", descricao: "Resolvido: ao excluir um lançamento, a lista atualiza imediatamente" },
    ]
  },
  {
    versao: "v9.0",
    data: "30/04/2026",
    destaque: false,
    titulo: "Painel Financeiro 100% em Tempo Real",
    emoji: "🔄",
    mudancas: [
      { tipo: "melhoria", descricao: "Ao salvar um lançamento, ele aparece na lista no mesmo instante" },
      { tipo: "melhoria", descricao: "Alterações feitas em outra aba do navegador aparecem automaticamente" },
      { tipo: "correcao", descricao: "Resolvido: lentidão ao salvar registros quando a internet estava instável" },
    ]
  },
  {
    versao: "v8.0",
    data: "29/04/2026",
    destaque: false,
    titulo: "Salvamento Mais Robusto e Confiável",
    emoji: "⏱️",
    mudancas: [
      { tipo: "melhoria", descricao: "Processo de salvar registros financeiros ficou mais seguro e confiável" },
      { tipo: "correcao", descricao: "Resolvido: conflitos entre internet rápida e lenta ao salvar dados" },
    ]
  },
  {
    versao: "v7.0",
    data: "28/04/2026",
    destaque: false,
    titulo: "Integração Completa do Fluxo Financeiro",
    emoji: "🔗",
    mudancas: [
      { tipo: "correcao", descricao: "Resolvido: botão Salvar não atualizava a lista de registros em alguns cenários" },
    ]
  },
  {
    versao: "v6.0",
    data: "27/04/2026",
    destaque: true,
    titulo: "Agora Você Trabalha Online e Offline sem Diferença",
    emoji: "🏗️",
    mudancas: [
      { tipo: "novo", descricao: "Sistema funciona perfeitamente mesmo sem internet — seus dados ficam salvos no dispositivo" },
      { tipo: "novo", descricao: "Quando a internet voltar, tudo sincroniza automaticamente com a nuvem" },
      { tipo: "offline", descricao: "Você pode cadastrar imóveis, clientes e transações mesmo offline" },
      { tipo: "performance", descricao: "Ações como salvar e editar respondem instantaneamente, sem esperar a rede" },
    ]
  },
  {
    versao: "v5.0",
    data: "Abril/2026",
    destaque: false,
    titulo: "Mais Segurança e Estabilidade",
    emoji: "🔐",
    mudancas: [
      { tipo: "seguranca", descricao: "Seus dados estão ainda mais protegidos com isolamento reforçado entre imobiliárias" },
      { tipo: "melhoria", descricao: "Fotos dos imóveis são preservadas ao editar informações" },
      { tipo: "correcao", descricao: "Resolvido: erros ao atualizar imóveis em situações específicas" },
    ]
  },
  {
    versao: "v1-v4",
    data: "Fevereiro-Março/2026",
    destaque: false,
    titulo: "Lançamento do Ilha Leads",
    emoji: "🏛️",
    mudancas: [
      { tipo: "novo", descricao: "Lançamento oficial da plataforma com todos os módulos essenciais" },
      { tipo: "novo", descricao: "Dashboard executivo com gráficos de vendas e aluguéis" },
      { tipo: "novo", descricao: "Gestão completa de imóveis com galeria de fotos e vitrine pública" },
      { tipo: "novo", descricao: "CRM de clientes com funil de vendas visual" },
      { tipo: "novo", descricao: "Módulo financeiro com relatórios profissionais em PDF e Excel" },
      { tipo: "novo", descricao: "Mensagens WhatsApp integradas direto no sistema" },
      { tipo: "novo", descricao: "Recomendações inteligentes de imóveis para seus clientes" },
      { tipo: "novo", descricao: "Personalize com sua marca: logo, cores, slogan e domínio próprio" },
      { tipo: "mobile", descricao: "Instale no celular como um aplicativo nativo (Android e iOS)" },
      { tipo: "offline", descricao: "Funciona sem internet — ideal para visitas e áreas com sinal fraco" },
      { tipo: "novo", descricao: "Calendário integrado com tarefas, visitas e compromissos" },
      { tipo: "novo", descricao: "Central de documentos para contratos e arquivos importantes" },
      { tipo: "seguranca", descricao: "Seus dados protegidos com criptografia e em conformidade com a LGPD" },
    ]
  },
];

// ============================================================
// 🧩 COMPONENTE PRINCIPAL
// ============================================================
export default function Changelog() {
  const isMobile = useIsMobile();
  const [filtro, setFiltro] = useState("todos");
  const [expandida, setExpandida] = useState(0);

  const tiposFiltro = useMemo(() => {
    const tipos = new Set();
    VERSOES.forEach(v => v.mudancas.forEach(m => tipos.add(m.tipo)));
    return ["todos", ...Array.from(tipos)];
  }, []);

  const versoesFiltradas = useMemo(() => {
    if (filtro === "todos") return VERSOES;
    return VERSOES.map(v => ({
      ...v,
      mudancas: v.mudancas.filter(m => m.tipo === filtro)
    })).filter(v => v.mudancas.length > 0);
  }, [filtro]);

  const totalMudancas = VERSOES.reduce((s, v) => s + v.mudancas.length, 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f8fafc" }}>
      
      <header 
        className="sticky top-0 z-50 backdrop-blur-xl border-b shadow-sm"
        style={{ 
          background: `linear-gradient(135deg, ${BRAND.primaryDark} 0%, ${BRAND.primary} 100%)`,
          borderColor: `${BRAND.gold}30`
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})` }}>
              <GitBranch size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg flex items-center gap-2">
                Novidades
                <Crown size={14} style={{ color: BRAND.gold }} fill={BRAND.gold} />
              </h1>
              <p className="text-xs text-white/70">
                {VERSOES.length} versões • {totalMudancas} atualizações
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-8">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`grid ${isMobile ? 'grid-cols-3 gap-2' : 'grid-cols-5 gap-3'} mb-8`}
        >
          {[
            { icon: Package, label: "Versões", value: VERSOES.length, color: BRAND.primary },
            { icon: Sparkles, label: "Novidades", value: VERSOES.reduce((s, v) => s + v.mudancas.filter(m => m.tipo === 'novo').length, 0), color: BRAND.purple },
            { icon: TrendingUp, label: "Melhorias", value: VERSOES.reduce((s, v) => s + v.mudancas.filter(m => m.tipo === 'melhoria').length, 0), color: BRAND.primaryLight },
            { icon: Bug, label: "Correções", value: VERSOES.reduce((s, v) => s + v.mudancas.filter(m => m.tipo === 'correcao').length, 0), color: BRAND.warning },
            { icon: Zap, label: "Performance", value: VERSOES.reduce((s, v) => s + v.mudancas.filter(m => m.tipo === 'performance').length, 0), color: BRAND.danger },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="bg-card rounded-xl p-3 text-center shadow-sm border"
                style={{ borderColor: `${stat.color}20` }}>
                <Icon size={isMobile ? 16 : 20} style={{ color: stat.color }} className="mx-auto mb-1" />
                <p className={`font-bold ${isMobile ? 'text-base' : 'text-xl'}`} style={{ color: BRAND.primaryDark }}>
                  {stat.value}
                </p>
                <p className="text-xs" style={{ color: "#64748b" }}>{stat.label}</p>
              </div>
            );
          })}
        </motion.div>

        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          {tiposFiltro.map(tipo => {
            const info = TIPOS[tipo];
            return (
              <motion.button
                key={tipo}
                whileTap={{ scale: 0.95 }}
                onClick={() => setFiltro(tipo)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 capitalize ${
                  filtro === tipo ? 'text-white shadow' : 'border bg-card'
                }`}
                style={filtro === tipo ? {
                  background: info ? `linear-gradient(135deg, ${info.color}, ${info.color}dd)` : `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`
                } : { borderColor: `${BRAND.primary}20`, color: "#64748b" }}
              >
                {tipo === "todos" ? "✨ Todos" : TIPOS[tipo]?.label || tipo}
              </motion.button>
            );
          })}
        </div>

        <div className="relative">
          <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5"
            style={{ background: `linear-gradient(180deg, ${BRAND.gold}40, ${BRAND.primary}20, transparent)` }} />

          <div className="space-y-8">
            {versoesFiltradas.map((versao, index) => {
              const expandido = expandida === index;
              
              return (
                <motion.div
                  key={versao.versao}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="relative pl-10 md:pl-14"
                >
                  <div className={`absolute left-2.5 md:left-4 w-3.5 h-3.5 md:w-4 md:h-4 rounded-full border-2 z-10 ${
                    versao.destaque ? 'border-2 shadow-lg' : ''
                  }`}
                    style={{ 
                      backgroundColor: versao.destaque ? BRAND.gold : 'white',
                      borderColor: versao.destaque ? BRAND.goldDark : BRAND.primary,
                      boxShadow: versao.destaque ? `0 0 12px ${BRAND.gold}60` : undefined
                    }} />

                  <div 
                    className={`rounded-2xl border transition-all cursor-pointer ${
                      versao.destaque ? 'shadow-lg' : 'shadow-sm'
                    }`}
                    style={{ 
                      backgroundColor: 'white',
                      borderColor: versao.destaque ? `${BRAND.gold}40` : `${BRAND.primary}15`,
                      borderWidth: versao.destaque ? '1.5px' : '1px'
                    }}
                    onClick={() => setExpandida(expandido ? null : index)}
                  >
                    <div className="px-4 md:px-6 py-4 flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-lg md:text-xl">{versao.emoji}</span>
                          <span className="font-bold text-sm md:text-base" style={{ color: BRAND.primaryDark }}>
                            {versao.titulo}
                          </span>
                          {versao.destaque && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
                              style={{ backgroundColor: `${BRAND.gold}20`, color: BRAND.goldDark }}>
                              Destaque
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs font-mono px-2 py-0.5 rounded-md"
                            style={{ backgroundColor: `${BRAND.primary}10`, color: BRAND.primary }}>
                            {versao.versao}
                          </span>
                          <span className="text-xs flex items-center gap-1" style={{ color: "#64748b" }}>
                            <Calendar size={12} />
                            {versao.data}
                          </span>
                          <span className="text-xs flex items-center gap-1" style={{ color: "#64748b" }}>
                            <GitBranch size={12} />
                            {versao.mudancas.length} mudanças
                          </span>
                        </div>
                      </div>
                      <motion.div
                        animate={{ rotate: expandido ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex-shrink-0 mt-1"
                      >
                        <ChevronRight size={18} style={{ color: BRAND.primary }} />
                      </motion.div>
                    </div>

                    <AnimatePresence>
                      {expandido && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 md:px-6 pb-4 space-y-1.5">
                            {versao.mudancas.map((mudanca, i) => {
                              const tipoInfo = TIPOS[mudanca.tipo] || TIPOS.novo;
                              const IconMudanca = tipoInfo.icon;
                              return (
                                <div key={i} className="flex items-start gap-2 py-1.5 px-3 rounded-lg"
                                  style={{ backgroundColor: tipoInfo.bg }}>
                                  <IconMudanca size={14} style={{ color: tipoInfo.color }} className="mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm" style={{ color: BRAND.primaryDark }}>
                                      {mudanca.descricao}
                                    </p>
                                  </div>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize flex-shrink-0"
                                    style={{ backgroundColor: `${tipoInfo.color}20`, color: tipoInfo.color }}>
                                    {tipoInfo.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 pt-8 border-t text-center"
          style={{ borderColor: `${BRAND.gold}20` }}
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield size={14} style={{ color: BRAND.success }} />
            <p className="text-xs font-medium" style={{ color: BRAND.success }}>
              Changelog público — sem detalhes técnicos internos
            </p>
          </div>
          <p className="text-sm" style={{ color: "#64748b" }}>
            Ilha Leads — Compromisso com a excelência
          </p>
          <div className="flex items-center justify-center gap-1 mt-1">
            <Star size={10} style={{ color: BRAND.gold }} fill={BRAND.gold} />
            <span className="text-xs" style={{ color: BRAND.gold }}>Atualizado em {new Date().toLocaleDateString('pt-BR')}</span>
            <Star size={10} style={{ color: BRAND.gold }} fill={BRAND.gold} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}